/**
 * Version-skew detection. The backend stamps every response with
 * X-Api-Version (its deploy SHA) and opens every SSE stream with a
 * deploy_version event. The first real version this tab observes becomes its
 * baseline; any different later version means the backend redeployed under us
 * and response shapes may have drifted — VersionSkewBar then offers a reload.
 * 'dev' is the no-deploy-identity sentinel: detection stays off.
 */
let baseline: string | null = null;
let skewed = false;
const listeners = new Set<() => void>();

export function recordApiVersionValue(version: string): void {
  // Boundary validation, not defensive code: the SSE deploy_version handshake
  // parses untrusted wire JSON and casts it to `{ version: string }` without
  // runtime validation — a malformed or missing field would otherwise corrupt
  // the skew baseline (e.g. record `undefined` as the first-seen version).
  if (typeof version !== 'string') return;
  if (version === 'dev') return;
  if (baseline === null) {
    baseline = version;
    return;
  }
  if (version !== baseline && !skewed) {
    skewed = true;
    listeners.forEach((cb) => cb());
  }
}

export function recordApiVersion(res: Response): void {
  const version = res.headers.get('X-Api-Version');
  if (version) recordApiVersionValue(version);
}

export function isSkewed(): boolean {
  return skewed;
}

/** Subscribe to the skew flip. Returns an unsubscribe function. */
export function onSkew(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function _resetForTests(): void {
  baseline = null;
  skewed = false;
  listeners.clear();
}
